<template>
  <RequiresPermission
    permission="view_terminals"
    fallback-route-name="merchantIndex"
  >
    <div class="space-y-4">
      <BaseCard
        v-if="
          organizationConfig?.terminalWebAppAuthRequired
        "
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h2
            class="text-sm font-semibold text-background-fg/87"
          >
            Open Terminal Web App
          </h2>
          <p class="mt-1 text-sm text-background-fg/60">
            Access an emulated terminal in your web browser
          </p>
        </div>
        <ButtonPrimary
          class="shrink-0 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isOpeningTerminalApp"
          @click="openTerminalApp"
        >
          {{
            isOpeningTerminalApp
              ? "Opening..."
              : "Open Terminal App"
          }}
        </ButtonPrimary>
      </BaseCard>
      <BaseCard class="relative">
        <QueryLoading v-if="isLoading" class="min-h-72" />
        <QueryError
          v-else-if="isTerminalsError"
          class="min-h-72"
          :show-retry="true"
          @retry="refetchTerminals"
        >
          Failed to retrieve terminals
        </QueryError>
        <template v-else>
          <div class="mb-2 flex w-full justify-center">
            <div class="flex-1"> </div>
            <SearchInput v-model="rawSearch" />
            <div
              class="flex flex-1 items-center justify-end"
            >
            </div>
          </div>
          <BaseTable
            v-model:ordering="ordering"
            :rows="data?.items ?? []"
            :columns="columns"
            :is-loading="isFetching"
          >
            <template #cell-name="{ row }">
              <BaseTableChevronLink
                v-if="row.clientId"
                :to="{
                  name: 'merchantDetail',
                  params: {
                    tenantId: row.clientId,
                  },
                  query: {
                    initialTab: 'Terminals',
                  },
                }"
              >
                {{ row.clientName }}
              </BaseTableChevronLink>
              <span v-else>{{ row.clientName }}</span>
            </template>
            <template #cell-lastPaired="{ row }">
              <template v-if="row.lastPaired">
                <div>
                  <p class="text-sm text-background-fg/87">
                    {{ formatToDate(row.lastPaired) }} at
                    {{ formatToTime(row.lastPaired) }}
                  </p>
                  <p class="text-xs text-background-fg/70">
                    {{
                      DateFns.formatDistance(
                        row.lastPaired,
                        now,
                        { addSuffix: true }
                      )
                    }}
                  </p>
                </div>
              </template>
            </template>
            <template #cell-unpairTerminal="{ row }">
              <div
                v-if="
                  showUnpairTerminalButton(
                    row,
                    permissions
                  ) ||
                  showConfirmTerminalButton(
                    row,
                    permissions
                  )
                "
                class="flex gap-4"
              >
                <BaseTableButton
                  v-if="
                    showUnpairTerminalButton(
                      row,
                      permissions
                    )
                  "
                  variant="danger"
                  @click="
                    handleOpenUnpairModal(row.terminalId)
                  "
                >
                  Unpair
                </BaseTableButton>
                <BaseTableButton
                  v-if="
                    showConfirmTerminalButton(
                      row,
                      permissions
                    )
                  "
                  variant="info"
                  @click="
                    handleOpenConfirmModal(row.terminalId)
                  "
                >
                  Confirm
                </BaseTableButton>
              </div>
              <!-- Required to avoid column.getCellValue default showing -->
              <span v-else />
            </template>
          </BaseTable>

          <div class="flex justify-end py-2">
            <BasePagination
              v-model="page"
              :page-count="pageCount"
            />
          </div>
        </template>
      </BaseCard>
    </div>
    <UnpairTerminalModal
      :is-open="unpairModalIsOpen"
      :terminal-id="selectedTerminalId"
      @close="unpairModalIsOpen = false"
    />
    <ConfirmTerminalModal
      :is-open="confirmModalIsOpen"
      :terminal-id="selectedTerminalId"
      @close="confirmModalIsOpen = false"
    />
  </RequiresPermission>
</template>

<script setup lang="ts">
import { useRequiredPermissions } from "@/composables/useRequiredPermissions"
import { createTerminalWebAppAccessToken } from "@/services/terminalWebApp/createTerminalWebAppAccessToken"
import type {
  TableColumn,
  TableOrdering,
} from "@/components/BaseTable.vue"
import type {
  ListTerminalsFilter,
  ListTerminalsOrdering,
} from "@/services/terminals/listTerminals"
import type { Terminal } from "@/services/terminals/listTerminals"
import ConfirmTerminalModal from "@/components/ConfirmTerminalModal.vue"
import type { UserPermission } from "@/services/auth/retrieveCurrentUser"

const { permissions } = useAuth()
const { hasRequiredPermissions } = useRequiredPermissions(
  "view_terminals"
)
const { organizationConfig } = useOrganizationConfig()

const rawSearch = ref("")
const isOpeningTerminalApp = ref(false)

const unpairModalIsOpen = ref(false)
const confirmModalIsOpen = ref(false)
const selectedTerminalId = ref("")

const debouncedSearch = debouncedRef(rawSearch, 300)

const reactiveFilters = reactive({ includeDemo: false })
const filters = computed<ListTerminalsFilter>(() => ({
  search: debouncedSearch.value.trim(),
  ...reactiveFilters,
}))

const { page, pageCount, pagination, resetPagination } =
  usePagination({
    count: computed(() => data.value?.count || 0),
    limit: 10,
  })

const ordering = ref<
  TableOrdering<ListTerminalsOrdering> | undefined
>()

watch(
  () => filters,
  () => resetPagination(),
  { deep: true }
)
const query = reactiveComputed(() => ({
  ...filters.value,
  ordering: ordering.value,
  ...pagination.value,
}))

const handleOpenUnpairModal = (terminalId: string) => {
  selectedTerminalId.value = terminalId
  unpairModalIsOpen.value = true
}
const handleOpenConfirmModal = (terminalId: string) => {
  selectedTerminalId.value = terminalId
  confirmModalIsOpen.value = true
}

const openTerminalApp = async () => {
  if (isOpeningTerminalApp.value) {
    return
  }

  isOpeningTerminalApp.value = true
  const terminalAppWindow = window.open(
    "about:blank",
    "_blank"
  )

  try {
    const { terminalWebAppAccessUrl } =
      await createTerminalWebAppAccessToken()

    if (terminalAppWindow) {
      terminalAppWindow.opener = null
      terminalAppWindow.location.href =
        terminalWebAppAccessUrl
    } else {
      // If new tab opening is blocked, fallback to current tab
      window.location.href = terminalWebAppAccessUrl
    }
  } catch {
    terminalAppWindow?.close()
    toast.error("Failed to open terminal app")
  } finally {
    isOpeningTerminalApp.value = false
  }
}

const {
  data,
  isLoading,
  isFetching,
  isError: isTerminalsError,
  refetch: refetchTerminals,
} = useTerminals(query, hasRequiredPermissions)

const now = useNow()

type ColumnId =
  | "terminalId"
  | "name"
  | "lastPaired"
  | "lastPairedVersion"
  | "registrationDate"
  | "unpairTerminal"

const columns: Array<
  TableColumn<Terminal, ColumnId, ListTerminalsOrdering>
> = [
  {
    id: "terminalId",
    header: "Serial Number",
    getCellValue: (row) => row.terminalId,
    isSortable: true,
  },
  {
    id: "name",
    header: "Client Name",
    getCellValue: (row) => row.clientName,
    isSortable: true,
  },
  {
    id: "lastPaired",
    header: "Last Paired",
    getCellValue: (row) => row.lastPaired,
    isSortable: true,
  },
  {
    id: "lastPairedVersion",
    header: "Last Paired Version",
    getCellValue: (row) => row.lastPairedVersion,
    isSortable: true,
  },
  {
    id: "registrationDate",
    header: "Registered",
    getCellValue: (row) =>
      row.registrationDate
        ? DateFns.formatDistance(
            row.registrationDate,
            now.value,
            {
              addSuffix: true,
            }
          )
        : "",
    isSortable: true,
  },
  {
    id: "unpairTerminal",
    header: "",
    getCellValue: (row) => row.terminalId,
    isSortable: false,
  },
]

const showUnpairTerminalButton = (
  terminal: Terminal,
  perms: Array<UserPermission>
) => terminal.isPaired && perms.includes("unpair_terminal")

const showConfirmTerminalButton = (
  terminal: Terminal,
  perms: Array<UserPermission>
) =>
  terminal.awaitingManualConfirmation &&
  perms.includes("confirm_terminal")
</script>
